import { defineChain } from 'viem';
import { base as baseOrigin, bscTestnet as bscTestnetOrigin, sepolia as sepoliaOrigin, mainnet as mainnetOrigin, bsc as bscOrigin } from 'viem/chains';

export const sepolia = defineChain({
  ...sepoliaOrigin,
  nativeCurrency: { name: 'Sepolia Ether', symbol: 'SepoliaETH', decimals: 18 },
  rpcUrls: {
    ...sepoliaOrigin.rpcUrls,
    default: {
      http: ['https://sepolia.drpc.org', 'https://ethereum-sepolia-rpc.publicnode.com'],
    },
  },
  iconUrl: `${import.meta.env?.VITE_VISUAL_URL}/Crypto/Chains/${sepoliaOrigin.id}.svg`,
  testnet: true,
});

export const bscTestnet = defineChain({
  ...bscTestnetOrigin,
  name: 'BNB Chain Testnet',
  rpcUrls: {
    ...bscTestnetOrigin.rpcUrls,
    default: {
      http: ['https://data-seed-prebsc-1-s1.bnbchain.org:8545', 'https://bsc-testnet-rpc.publicnode.com', 'https://bsc-testnet.drpc.org'],
    },
  },
  iconUrl: `${import.meta.env?.VITE_VISUAL_URL}/Crypto/Chains/${bscTestnetOrigin.id}.svg`,
  testnet: true,
});

export const mainnet = defineChain({
  ...mainnetOrigin,
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: {
    ...mainnetOrigin.rpcUrls,
    default: {
      http: ['https://eth.merkle.io', 'https://ethereum-rpc.publicnode.com', 'https://eth.drpc.org'],
    },
  },
  iconUrl: `${import.meta.env?.VITE_VISUAL_URL}/Crypto/Chains/${mainnetOrigin.id}.svg`,
});

export const bsc = defineChain({
  ...bscOrigin,
  name: 'BNB Chain',
  rpcUrls: {
    ...bscOrigin.rpcUrls,
    default: {
      http: ['https://bsc-dataseed.bnbchain.org', 'https://bsc-dataseed.defibit.io', 'https://bsc.nodereal.io'],
    },
  },
  iconUrl: `${import.meta.env?.VITE_VISUAL_URL}/Crypto/Chains/${bscOrigin.id}.svg`,
});

export const base = defineChain({
  ...baseOrigin,
  name: 'Base',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: {
    ...baseOrigin.rpcUrls,
    default: {
      http: ['https://mainnet.base.org', 'https://base-rpc.publicnode.com', 'https://base.drpc.org'],
    },
  },
  iconUrl: `${import.meta.env?.VITE_VISUAL_URL}/Crypto/Chains/${baseOrigin.id}.svg`,
});

export const AllSupportedChains = [mainnet, bsc, base, sepolia, bscTestnet] as const;

export const MainnetSupported = import.meta.env?.VITE_ENABLE_MAINNET === 'true';
export const TestnetSupported = !MainnetSupported || import.meta.env?.VITE_ENABLE_TESTNET === 'true';

export const SupportedChains: typeof AllSupportedChains = AllSupportedChains.filter((chain) => {
  if (TestnetSupported && chain.testnet) return true;
  if (MainnetSupported && !chain.testnet) return true;
  return false;
}) as any;

export const SupportedChainMap = Object.fromEntries(SupportedChains.map((chain) => [chain.id, chain])) as Record<SupportedChainId, (typeof SupportedChains)[number]>;

export type SupportedChain = (typeof SupportedChains)[number];

export type SupportedChainId = SupportedChain['id'];

export const SupportedChainIds = SupportedChains.map((chain) => chain.id) as number[];

export const isSupportedChainId = (chainId: number): chainId is SupportedChainId => {
  return SupportedChainIds.includes(chainId);
};

export const defaultChain = SupportedChains[0];

export const defaultChainId = defaultChain.id;
